import { motion } from "framer-motion";
import { Drumstick, WheatOff, Sprout, HeartPulse, GlassWater, MapPin } from "lucide-react";
import { EASE } from "@/lib/scroll";

const MEAT_IMG = "/images/product-ingredients.webp";

const CARDS = [
  {
    icon: WheatOff,
    badge: "Hipoalergenic",
    title: "Grain Free — Zero Cereale",
    desc: "Formulă biologic adecvată sistemului digestiv strict carnivor al felinei. Fără gluten, soia sau amidon în exces.",
  },
  {
    icon: Sprout,
    badge: "Superaliment",
    title: "Dovleac Proaspăt",
    desc: "Bogat în beta-caroten, antioxidanți naturali și fibre solubile pentru un tranzit digestiv optim.",
  },
  {
    icon: HeartPulse,
    badge: "Vitalitate",
    title: "Taurină Esențială",
    desc: "Dozaj precis pentru vederea ageră, funcția cardiacă impecabilă și un sistem imunitar de fier.",
  },
  {
    icon: GlassWater,
    badge: "100% Inert & Sustenabil",
    title: "Ambalaj Nobil din Sticlă",
    desc: "Păstrează gustul pur, suculența naturală și aroma intactă — fără transfer de compuși din conserve metalice sau plastic.",
  },
];

const cardAnim = (i) => ({
  initial: { opacity: 0, y: 44 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.9, ease: EASE, delay: (i % 3) * 0.12 },
});

export default function Benefits() {
  return (
    <section id="beneficii" className="relative py-24 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16 flex flex-col gap-6 lg:mb-20 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-12 bg-gold/70" />
              <p className="font-mono text-[10px] uppercase tracking-[0.38em] text-gold sm:text-[11px]">
                Beneficii super premium
              </p>
            </div>
            <h2 className="max-w-xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
              Nutriție super premium, <em className="text-gold-gradient italic">fără compromisuri.</em>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-stone lg:text-base">
            Fiecare borcan NOSA reunește ingrediente alese cu discernământ, pentru o pisică
            sănătoasă, hidratată și plină de vigoare.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-6">
          <motion.div
            {...cardAnim(0)}
            className="group relative min-h-[360px] overflow-hidden rounded-3xl border border-gold/15 md:col-span-4"
            data-testid="benefit-card-chicken"
          >
            <img
              src={MEAT_IMG}
              alt="Borcan NOSA alături de piept de pui proaspăt, dovleac și ingrediente naturale"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.8s] ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/10" />
            <div className="relative flex h-full flex-col justify-end p-8 lg:p-10">
              <span className="mb-4 w-fit rounded-full border border-gold/40 px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.24em] text-gold">
                Monoproteină pură
              </span>
              <div className="flex items-end justify-between gap-6">
                <h3 className="max-w-xs font-serif text-3xl font-light leading-tight text-cream lg:text-4xl">
                  85% Carne Curată de Pui
                </h3>
                <Drumstick className="mb-1 h-8 w-8 shrink-0 text-gold" strokeWidth={1.4} />
              </div>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-stone lg:text-base">
                Conținut excepțional de proteine de înaltă biodisponibilitate, fără făinuri
                animale secundare sau subproduse ascunse.
              </p>
            </div>
          </motion.div>

          {CARDS.map((c, i) => (
            <motion.div
              key={c.title}
              {...cardAnim(i + 1)}
              className={`group rounded-3xl border border-gold/15 bg-card p-8 transition-all duration-500 hover:border-gold/50 hover:bg-[#211f1a] ${
                i === 0 ? "md:col-span-2" : "md:col-span-2"
              }`}
              data-testid={`benefit-card-${i}`}
            >
              <div className="flex h-full flex-col">
                <div className="mb-8 flex items-center justify-between">
                  <c.icon className="h-7 w-7 text-gold transition-transform duration-500 group-hover:scale-110" strokeWidth={1.4} />
                  <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-gold/50">
                    {c.badge}
                  </span>
                </div>
                <h3 className="font-serif text-2xl font-light leading-snug text-cream">
                  {c.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-stone">{c.desc}</p>
              </div>
            </motion.div>
          ))}

          <motion.div
            {...cardAnim(1)}
            className="group relative flex items-center justify-between overflow-hidden rounded-3xl border border-gold/25 bg-gradient-to-r from-card to-ink p-8 md:col-span-6 lg:p-10"
            data-testid="benefit-card-romania"
          >
            <span
              className="pointer-events-none absolute -right-4 -top-8 select-none font-serif text-[7rem] font-light leading-none text-gold/[0.07] lg:text-[11rem]"
              aria-hidden="true"
            >
              RO
            </span>
            <div className="relative flex items-center gap-6">
              <MapPin className="h-8 w-8 shrink-0 text-gold" strokeWidth={1.4} />
              <div>
                <h3 className="font-serif text-2xl font-light text-cream lg:text-3xl">
                  Fabricat cu Mândrie în România
                </h3>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-stone">
                  Produs local conform celor mai exigente standarde europene de siguranță
                  alimentară și prospețime nealterată.
                </p>
              </div>
            </div>
            <span className="relative hidden rounded-full border border-gold/40 px-5 py-2 font-mono text-[9px] uppercase tracking-[0.24em] text-gold sm:block">
              Origine garantată
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
